<div class="container-fluid h-100 px-4 px-md-5">
  <div class="my-4">

    <div class="page-header">
      <button class="back-btn" (click)="goBack()" type="button">← All issues</button>
    </div>

    <div *ngIf="loading" class="dashboard-loading">
      <div class="dashboard-spinner"></div>
    </div>

    <div *ngIf="!loading && error" class="state-msg state-msg--error">Failed to load this issue.</div>

    <ng-container *ngIf="!loading && !error && issue">

      <!-- Headline ----------------------------------------------------- -->
      <div class="issue-header">
        <div class="issue-header__main">
          <div class="issue-header__badges">
            <span class="level-badge" [ngClass]="'level-badge--' + issue.level">{{ issue.level }}</span>
            <span class="platform-badge">{{ issue.platform }}</span>
            <span class="status-badge" [ngClass]="'status-badge--' + issue.status">{{ issue.status }}</span>
            <span class="platform-badge" *ngIf="issue.environment">{{ issue.environment }}</span>
            <span class="platform-badge" *ngIf="issue.release">{{ issue.release }}</span>
          </div>
          <h1 class="issue-header__title">{{ issue.error_type || 'Message' }}</h1>
          <p class="issue-header__value">{{ issue.value }}</p>
          <p class="issue-header__culprit" *ngIf="issue.culprit">{{ issue.culprit }}</p>
        </div>

        <div class="issue-header__actions">
          <button class="pw-btn pw-btn--primary" type="button" [disabled]="updating"
            *ngIf="issue.status !== 'resolved'" (click)="setStatus('resolved')">Resolve</button>
          <button class="pw-btn pw-btn--ghost" type="button" [disabled]="updating"
            *ngIf="issue.status === 'resolved'" (click)="setStatus('unresolved')">Reopen</button>
          <button class="pw-btn pw-btn--ghost" type="button" [disabled]="updating"
            *ngIf="issue.status !== 'ignored'" (click)="setStatus('ignored')">Ignore</button>
          <button class="pw-btn pw-btn--ghost" type="button" [disabled]="updating"
            *ngIf="issue.status === 'ignored'" (click)="setStatus('unresolved')">Un-ignore</button>
          <button class="pw-btn pw-btn--danger-outline" type="button" (click)="deleteIssue()">Delete</button>
        </div>
      </div>

      <div class="stats-row">
        <div class="stat-card">
          <span class="stat-card__label">Events</span>
          <span class="stat-card__value">{{ issue.times_seen | number }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">Users affected</span>
          @if (noUserReported) {
            <span class="stat-card__value stat-card__value--muted" data-testid="issue-users-unknown">—</span>
            <span class="stat-card__sub">
              No user reported.
              <a [routerLink]="userSetupGuide.link" data-testid="issue-users-help">{{ userSetupGuide.label }}</a>
            </span>
          } @else {
            <span class="stat-card__value" data-testid="issue-users">{{ issue.users_seen | number }}</span>
          }
        </div>
        <div class="stat-card">
          <span class="stat-card__label">First seen</span>
          <span class="stat-card__value stat-card__value--small">{{ relativeTime(issue.first_seen) }}</span>
          <span class="stat-card__sub">{{ formatDateTime(issue.first_seen) }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">Last seen</span>
          <span class="stat-card__value stat-card__value--small">{{ relativeTime(issue.last_seen) }}</span>
          <span class="stat-card__sub">{{ formatDateTime(issue.last_seen) }}</span>
        </div>
      </div>

      <div *ngIf="!event" class="state-msg">No event payload stored for this issue.</div>

      <ng-container *ngIf="event">

        <!-- Stack trace -------------------------------------------------- -->
        <section class="panel" *ngIf="event.stacktrace?.length">
          <div class="panel__header">
            <h2 class="panel__title">Stack trace</h2>
            <button class="toggle-btn" type="button" (click)="showAllFrames = !showAllFrames"
              *ngIf="hiddenFrameCount > 0 || showAllFrames">
              {{ showAllFrames ? 'Show app frames only' : 'Show all frames (' + hiddenFrameCount + ' hidden)' }}
            </button>
          </div>

          <div class="frame" *ngFor="let frame of frames" [class.frame--vendor]="!frame.in_app">
            <div class="frame__header">
              <span class="frame__filename">{{ frame.filename || frame.module }}</span>
              <span class="frame__function">in {{ frame.function || '?' }}</span>
              <span class="frame__lineno" *ngIf="frame.lineno">line {{ frame.lineno }}</span>
              <span class="frame__tag" *ngIf="frame.in_app">app</span>
              <span class="frame__tag frame__tag--mapped" *ngIf="frame.minified_filename"
                [title]="'Minified: ' + frame.minified_filename + ':' + frame.minified_lineno">
                source map
              </span>
            </div>

            <div class="frame__context" *ngIf="frame.context_line">
              <div class="frame__line" *ngFor="let line of contextLines(frame)"
                [class.frame__line--error]="line.isError">
                <span class="frame__line-no">{{ line.lineno }}</span>
                <code class="frame__line-text">{{ line.text }}</code>
              </div>
            </div>
          </div>
        </section>

        <!-- Request ------------------------------------------------------ -->
        <section class="panel" *ngIf="event.request?.url || event.url">
          <h2 class="panel__title">Request</h2>
          <table class="kv-table">
            <tbody>
              <tr *ngIf="event.request?.method">
                <th>Method</th>
                <td>{{ event.request.method }}</td>
              </tr>
              <tr>
                <th>URL</th>
                <td class="break">{{ event.request?.url || event.url }}</td>
              </tr>
              <tr *ngIf="event.request?.query_string">
                <th>Query</th>
                <td class="break">{{ event.request.query_string }}</td>
              </tr>
              <tr *ngIf="event.transaction">
                <th>Transaction</th>
                <td>{{ event.transaction }}</td>
              </tr>
              <tr *ngFor="let header of headerEntries">
                <th>{{ header.key }}</th>
                <td class="break">{{ header.value }}</td>
              </tr>
            </tbody>
          </table>

          <pre class="snippet" *ngIf="requestBody">{{ requestBody }}</pre>
        </section>

        <!-- Context ------------------------------------------------------ -->
        <div class="panel-row">
          <section class="panel panel--half" *ngIf="userEntries.length">
            <h2 class="panel__title">User</h2>
            <table class="kv-table">
              <tbody>
                <tr *ngFor="let entry of userEntries">
                  <th>{{ entry.key }}</th>
                  <td class="break">{{ entry.value }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="panel panel--half" *ngIf="tagEntries.length || extraEntries.length">
            <h2 class="panel__title">Tags & extra</h2>
            <table class="kv-table">
              <tbody>
                <tr *ngFor="let entry of tagEntries">
                  <th>{{ entry.key }}</th>
                  <td class="break">{{ entry.value }}</td>
                </tr>
                <tr *ngFor="let entry of extraEntries">
                  <th>{{ entry.key }}</th>
                  <td class="break">{{ entry.value }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <!-- Breadcrumbs -------------------------------------------------- -->
        <section class="panel" *ngIf="event.breadcrumbs?.length">
          <h2 class="panel__title">Breadcrumbs</h2>
          <div class="crumb" *ngFor="let crumb of event.breadcrumbs">
            <span class="crumb__category">{{ crumb.category }}</span>
            <span class="crumb__message">{{ crumb.message }}</span>
            <time class="crumb__time" [attr.datetime]="crumb.timestamp" [title]="crumb.timestamp">{{ crumbTime(crumb.timestamp) }}</time>
          </div>
        </section>

        <!-- SDK ---------------------------------------------------------- -->
        <section class="panel">
          <h2 class="panel__title">Event</h2>
          <table class="kv-table">
            <tbody>
              <tr>
                <th>Event ID</th>
                <td class="break">{{ event.uuid }}</td>
              </tr>
              <tr>
                <th>Happened at</th>
                <td>{{ formatDateTime(event.timestamp) }}</td>
              </tr>
              <tr>
                <th>Received at</th>
                <td>{{ formatDateTime(event.received_at) }}</td>
              </tr>
              <tr *ngIf="event.server_name">
                <th>Server</th>
                <td>{{ event.server_name }}</td>
              </tr>
              <tr *ngIf="event.sdk_name">
                <th>SDK</th>
                <td>{{ event.sdk_name }} {{ event.sdk_version }}</td>
              </tr>
              <tr *ngIf="event.trace_id">
                <th>Trace</th>
                <td>
                  <a *ngIf="traceTransactionId"
                    [routerLink]="['/main', 'project', projectId, 'tracing', traceTransactionId]">
                    See the request this happened in
                  </a>
                  <span *ngIf="!traceTransactionId" class="mono text-muted">{{ event.trace_id }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

      </ng-container>

      <!-- Event list ---------------------------------------------------- -->
      <section class="panel" *ngIf="events.length">
        <h2 class="panel__title">Recent events ({{ eventCount | number }})</h2>
        <table class="table table-sm events-table">
          <tbody>
            <tr *ngFor="let summary of events" (click)="selectEvent(summary)"
              [class.events-table__row--active]="event?.uuid === summary.uuid">
              <td>{{ formatDateTime(summary.timestamp) }}</td>
              <td class="text-muted">{{ summary.environment || '—' }}</td>
              <td class="text-muted">{{ summary.release || '—' }}</td>
              <td class="text-muted break">{{ summary.transaction || summary.url || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </section>

    </ng-container>

  </div>
</div>
